<!--
	Ported from analytics-design/src/components/CoverageDashboard.tsx. The design's own
	<FilterBar pageTitle="Coverage" /> is not rendered here - AnalyticsPanelComponent already
	mounts one shared FilterBar above every screen, so this breadcrumb/context bar ends up below
	the filter bar instead of above it, as it was in the original. Structural changes: the
	Media x Brand Matrix is a dynamic column loop (any number of selected brands, not a fixed 6),
	and the Coverage Calendar renders the selected period's real days (not a fixed Q2-2025 grid).

	SETTE redesign (CLIPPING-598): token-by-token restyle, "Reach" -> "Visibility" in all copy,
	and two Правило Г/Принцип 3 fixes - the Media x Brand matrix recolors by mention-count
	intensity (SEQ_COLORS) instead of by impact sign, and both gradient legend bars (matrix,
	calendar) become discrete solid swatches.
-->
<div class="space-y-4">
	@if (showSkeleton) {
	<app-analytics-skeleton [kpiCount]="4" [chartCount]="3"></app-analytics-skeleton>
	} @else if (loadError) {
	<app-analytics-error-state [error]="errorState" (retry)="fetch()"></app-analytics-error-state>
	} @else if (isEmptyPeriod) {
	<app-analytics-empty-state variant="no-mentions"></app-analytics-empty-state>
	} @else {
	<div class="space-y-4" [class.opacity-50]="isRefreshing" [class.pointer-events-none]="isRefreshing">

	<!-- 1. TOP BREADCRUMB & CONTEXT -->
	<div class="bg-surface px-4 py-3 rounded-2xl border border-line flex flex-col md:flex-row md:items-center justify-between gap-3">
		<div class="overline-label text-ink">
			<span>{{ 'ANALYTICS_NAV.COVERAGE_DASHBOARD' | translate }}</span>
		</div>

		<div class="flex items-center gap-2 caption text-ink-subtle font-normal">
			<span class="inline-flex items-center gap-1 bg-blue-tint text-blue px-2 py-0.5 rounded-full font-medium">
				<mat-icon class="!w-3 !h-3 !text-xs">layers</mat-icon>
				<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.COVERAGE_QUALITY_BADGE' | translate }}</span>
			</span>
			<span class="hidden lg:inline text-ink-subtle">· {{ filters.dateRange }}</span>
		</div>
	</div>

	<!-- ROW 0: KPI CARDS -->
	<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
		<app-kpi-tile
			[label]="'ANALYTICS_GLOSSARY.MENTIONS' | translate"
			infoKey="ANALYTICS_INFO.COMMON.MENTIONS"
			[topRightNote]="marketTotalNote"
			[value]="mentionsKpi.value"
			[deltaLabel]="mentionsKpi.deltaLabel"
			[trendUp]="mentionsKpi.trendUp"
			[sparklineValues]="sparkMentions"
			[sparklineColor]="sparklineColorFor(mentionsKpi.trendUp)"
			[sampleSize]="data?.mentions?.sampleSize"
></app-kpi-tile>

		<app-kpi-tile
			[label]="'ANALYTICS_COVERAGE_DASHBOARD.AVG_VISIBILITY_PER_MENTION' | translate"
			infoKey="ANALYTICS_INFO.COMMON.VISIBILITY"
			[topRightNote]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_SUBLABEL' | translate"
			[value]="avgReachKpi.value"
			[deltaLabel]="avgReachKpi.deltaLabel"
			[trendUp]="avgReachKpi.trendUp"
			[sparklineValues]="sparkReach"
			[sparklineColor]="sparklineColorFor(avgReachKpi.trendUp)"
			[sampleSize]="data?.avgReach?.sampleSize"
></app-kpi-tile>

		<app-kpi-tile
			[label]="'ANALYTICS_COVERAGE_DASHBOARD.AVG_IMPACT_PER_MENTION' | translate"
			infoKey="ANALYTICS_INFO.COMMON.IMPACT"
			[topRightNote]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SUBLABEL' | translate"
			[value]="avgImpactKpi.value"
			valueSuffix="pts"
			valueSuffixClass="caption text-ink-subtle"
			[deltaLabel]="avgImpactKpi.deltaLabel"
			[trendUp]="avgImpactKpi.trendUp"
			[sparklineValues]="sparkImpactSigned"
			[sparklineColor]="sparklineColorFor(avgImpactKpi.trendUp)"
			[sparklineZeroLine]="true"
			[sampleSize]="data?.avgImpact?.sampleSize"
></app-kpi-tile>

		<app-kpi-tile
			[label]="'ANALYTICS_COVERAGE_DASHBOARD.SHARE_OF_VOICE_VOLUME' | translate"
			infoKey="ANALYTICS_INFO.COMMON.SHARE_OF_VOICE"
			[topRightNote]="ourBrandNote"
			[value]="shareOfVoiceKpi.value"
			[deltaLabel]="shareOfVoiceKpi.deltaLabel"
			[trendUp]="shareOfVoiceKpi.trendUp"
			[sparklineColor]="sparklineColorFor(shareOfVoiceKpi.trendUp)"
			[sampleSize]="data?.shareOfVoice?.sampleSize"
></app-kpi-tile>
	</div>

	<!-- ROW 1: THREE TREND CHARTS SIDE-BY-SIDE -->
	<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
		<!-- CHART 1: Coverage Trend -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[340px]">
			<div>
				<div class="h-6 flex items-center justify-between mb-1.5">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.COVERAGE_TREND_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.COVERAGE_TREND"></app-info-icon>
					</div>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.VOLUME_COUNTS' | translate }}</span>
				</div>

				<div class="h-5 flex items-center justify-between caption text-ink-subtle font-normal mb-2 border-b border-line pb-1.5">
					<span class="flex items-center gap-1 font-medium text-blue">
						<span class="w-2.5 h-1 bg-blue rounded-full"></span> {{ 'ANALYTICS_COVERAGE_DASHBOARD.TOTAL_MENTIONS_LEGEND' | translate }}
					</span>
					<span class="caption text-ink-subtle font-normal">{{ filters.dateRange }}</span>
				</div>
			</div>

			<div class="relative w-full h-[210px]">
				<app-chart type="line" [data]="coverageTrendChartData" [options]="coverageTrendChartOptions" height="210px"></app-chart>
			</div>

			<div class="h-6 flex items-center justify-between caption text-ink-subtle font-normal pt-2 border-t border-line">
				@if (mentionsSampleSize !== undefined) {
				<app-sample-size-note [count]="mentionsSampleSize"></app-sample-size-note>
				}
				<button type="button" (click)="onNavigate('mentions')" class="text-blue font-medium hover:underline flex items-center gap-0.5 cursor-pointer">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.VIEW_ALL_MENTIONS' | translate }}</span>
					<span>→</span>
				</button>
			</div>
		</div>

		<!-- CHART 5: Visibility Trend -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[340px]">
			<div>
				<div class="h-6 flex items-center justify-between mb-1.5">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.VISIBILITY_TREND_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.VISIBILITY_TREND"></app-info-icon>
					</div>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_GLOSSARY.AVG_VISIBILITY_SUBLABEL' | translate }}</span>
				</div>

				<div class="h-5 flex items-center justify-between caption text-ink-subtle font-normal mb-2 border-b border-line pb-1.5">
					<span class="flex items-center gap-1 font-medium text-ink">
						<span class="w-2.5 h-1 bg-chart-4 rounded-full"></span> {{ 'ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX' | translate }}
					</span>
					<span class="caption text-ink-subtle font-normal">{{ filters.dateRange }}</span>
				</div>
			</div>

			<div class="relative w-full h-[210px]">
				<app-chart type="line" [data]="reachTrendChartData" [options]="reachTrendChartOptions" height="210px"></app-chart>
			</div>

			<div class="h-6 flex items-center justify-between caption text-ink-subtle font-normal pt-2 border-t border-line">
				@if (avgReachSampleSize !== undefined) {
				<app-sample-size-note [count]="avgReachSampleSize"></app-sample-size-note>
				}
				<button type="button" (click)="onNavigate('reach')" class="text-blue font-medium hover:underline flex items-center gap-0.5 cursor-pointer">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.VIEW_VISIBILITY_ANALYSIS' | translate }}</span>
					<span>→</span>
				</button>
			</div>
		</div>

		<!-- CHART 6: Impact Trend -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[340px]">
			<div>
				<div class="h-6 flex items-center justify-between mb-1.5">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.IMPACT_TREND_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.IMPACT_TREND"></app-info-icon>
					</div>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_GLOSSARY.AVG_IMPACT_SUBLABEL' | translate }}</span>
				</div>

				<div class="h-5 flex items-center justify-between caption text-ink-subtle font-normal mb-2 border-b border-line pb-1.5">
					<span class="font-medium text-ink">{{ 'ANALYTICS_COVERAGE_DASHBOARD.WEEKLY_NET_IMPACT' | translate }}</span>
					<span class="caption text-ink-subtle font-normal">{{ 'ANALYTICS_COVERAGE_DASHBOARD.ZERO_BASELINE_NOTE' | translate }}</span>
				</div>
			</div>

			<div class="relative w-full h-[210px]">
				<app-chart type="line" [data]="impactTrendChartData" [options]="impactTrendChartOptions" height="210px"></app-chart>
			</div>

			<div class="h-6 flex items-center justify-between caption text-ink-subtle font-normal pt-2 border-t border-line">
				@if (avgImpactSampleSize !== undefined) {
				<app-sample-size-note [count]="avgImpactSampleSize"></app-sample-size-note>
				}
				<button type="button" (click)="onNavigate('impact')" class="text-blue font-medium hover:underline flex items-center gap-0.5 cursor-pointer">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.VIEW_IMPACT_BREAKDOWN' | translate }}</span>
					<span>→</span>
				</button>
			</div>
		</div>
	</div>

	<!-- ROW 2: COMPARISON BLOCK -->
	<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
		<!-- CHART 2: Brand Coverage Comparison -->
		<div class="lg:col-span-2 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[320px]">
			<div>
				<div class="flex items-center justify-between mb-2">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.BRAND_COMPARISON_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.BRAND_COMPARISON"></app-info-icon>
					</div>
					<div class="flex bg-surface-gray rounded-full p-1 caption">
						@for (metric of brandMetricOptions; track metric) {
						<button
							type="button"
							(click)="setBrandMetric(metric)"
							class="px-2.5 py-0.5 rounded-full font-medium transition-colors cursor-pointer"
							[class]="brandMetric === metric ? 'bg-surface text-ink shadow-[0_1px_3px_rgba(28,29,33,0.10)]' : 'text-ink-muted hover:text-ink'">
							{{ 'ANALYTICS_COVERAGE_DASHBOARD.BY_PREFIX' | translate }} {{ brandMetricLabel(metric) }}
						</button>
						}
					</div>
				</div>
			</div>

			<div class="overflow-x-auto flex-1 my-1">
				<table class="w-full text-left text-[11px] border-collapse">
					<thead>
						<tr class="overline-label text-ink-subtle border-b border-line bg-page">
							<th class="h-10 px-2.5">{{ 'ANALYTICS_GLOSSARY.BRAND' | translate }}</th>
							<th class="h-10 px-2.5 min-w-[200px]">{{ 'ANALYTICS_COVERAGE_DASHBOARD.TOTAL_MENTIONS_CHANGE' | translate }}</th>
							<th class="h-10 px-2.5 text-center">{{ 'ANALYTICS_COVERAGE_DASHBOARD.AVG_VISIBILITY_HEADER' | translate }}</th>
							<th class="h-10 px-2.5 text-right">{{ 'ANALYTICS_COVERAGE_DASHBOARD.AVG_IMPACT_HEADER' | translate }}</th>
						</tr>
					</thead>
					<tbody>
						@for (item of sortedBrandData; track item.brand) {
						<tr class="h-[52px] border-t border-line hover:bg-surface-blue transition-colors">
							<td class="px-2.5 font-medium text-ink">
								<div class="flex items-center gap-2">
									<span class="w-2.5 h-2.5 rounded-full shrink-0" [style.background-color]="item.color"></span>
									<span class="truncate">{{ item.brand }}</span>
									@if (item.isClient) {
									<span class="text-[9px] bg-blue text-on-blue font-medium px-1.5 py-0.2 rounded-full">{{ 'ANALYTICS_GLOSSARY.CLIENT' | translate }}</span>
									}
								</div>
							</td>
							<td class="px-2.5">
								<div class="flex items-center gap-2">
									<div class="flex-1 bg-neutral-tint rounded-full h-3 overflow-hidden">
										<div class="h-full rounded-full transition-all" [style.width.%]="(item.value / maxBrandValue) * 100" [style.background-color]="item.color"></div>
									</div>
									<div class="flex items-center gap-1.5">
										<span class="font-medium text-ink text-xs w-6 text-right tabular-nums">{{ fmtInt(item.value) }}</span>
										<span class="caption text-ink-subtle font-normal">{{ item.changeStr }}</span>
									</div>
								</div>
							</td>
							<td class="px-2.5 text-center">
								<div class="flex items-center justify-center gap-1">
									<span class="font-medium text-ink bg-surface-gray px-2 py-0.5 rounded-full text-[11px] tabular-nums">
										{{ item.avgReach }}
									</span>
									@if (item.isLowData) {
									<span class="text-[9px] bg-warning-tint text-warning px-1 py-0.2 rounded-full font-medium" [title]="'ANALYTICS_COVERAGE_DASHBOARD.LOW_SAMPLE_SIZE_TOOLTIP' | translate">
										&lt;10
									</span>
									}
								</div>
							</td>
							<td class="px-2.5 text-right">
								<span class="inline-block font-medium px-2 py-0.5 rounded-full text-[11px] tabular-nums" [class]="toneClasses(item.impactTone)">
									{{ item.avgImpact }}
								</span>
							</td>
						</tr>
						}
						@if (!loading && sortedBrandData.length === 0) {
						<tr><td colspan="4" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.NO_BRAND_DATA' | translate }}</td></tr>
						}
					</tbody>
				</table>
			</div>

			<div class="flex items-center justify-between caption text-ink-subtle font-normal pt-2 border-t border-line">
				<span>{{ brandSummaryLine }}</span>
				<button type="button" (click)="onNavigate('brands')" class="text-blue font-medium hover:underline flex items-center gap-0.5 cursor-pointer">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.FULL_BRAND_COMPARISON' | translate }}</span>
					<span>→</span>
				</button>
			</div>
		</div>

		<!-- CHART 3: Media Type -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[320px]">
			<div>
				<div class="flex items-center justify-between mb-2">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.MEDIA_TYPE_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.MEDIA_TYPE"></app-info-icon>
					</div>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.VOLUME_DISTRIBUTION' | translate }}</span>
				</div>
			</div>

			<div class="relative w-full h-[135px] flex items-center justify-center">
				<app-chart type="doughnut" [data]="mediaTypeChartData" [options]="mediaTypeChartOptions" height="135px"></app-chart>
				<div class="absolute text-center pointer-events-none">
					<div class="text-xs font-medium text-ink tabular-nums">{{ mediaTypeTotal }}</div>
					<div class="overline-label text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.TOTAL_LABEL' | translate }}</div>
				</div>
			</div>

			@if (mediaTypeTotal > 0) {
			<app-sample-size-note class="block text-center" [count]="mediaTypeTotal"></app-sample-size-note>
			}

			<div class="space-y-1 text-[10px] mt-1 border-t border-line pt-1.5">
				@for (m of mediaTypeData; track m.name) {
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-1.5">
						<span class="w-2 h-2 rounded-full" [style.background-color]="m.color"></span>
						<span class="text-ink-muted font-medium">{{ m.name }}</span>
					</div>
					<span class="font-medium text-ink tabular-nums">{{ m.percentage }} <span class="text-ink-subtle font-normal">({{ fmtInt(m.value) }})</span></span>
				</div>
				}
				@if (!loading && mediaTypeData.length === 0) {
				<div class="text-ink-subtle text-center py-2">{{ 'ANALYTICS_COVERAGE_DASHBOARD.NO_MEDIA_TYPE_DATA' | translate }}</div>
				}
			</div>

			<div class="flex items-center justify-end caption text-ink-subtle font-normal pt-2 border-t border-line">
				<span class="flex-1">{{ mediaTypeSummaryLine }}</span>
				<button type="button" (click)="onNavigate('media')" class="text-blue font-medium hover:underline flex items-center gap-0.5 cursor-pointer shrink-0">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.VIEW_MEDIA_CHANNELS' | translate }}</span>
					<span>→</span>
				</button>
			</div>
		</div>
	</div>

	<!-- ROW 3: MEDIA AND PEOPLE -->
	<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
		<!-- CHART 7: Media x Brand Matrix Heatmap -->
		<div class="lg:col-span-2 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[320px]">
			<div>
				<div class="flex items-center justify-between mb-2">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.MEDIA_BRAND_MATRIX_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.MEDIA_BRAND_MATRIX"></app-info-icon>
					</div>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.VOLUME_HEATMAP' | translate }}</span>
				</div>
			</div>

			<div class="overflow-x-auto flex-1 my-1">
				<table class="w-full text-center text-[10px] border-collapse">
					<thead>
						<tr class="overline-label text-ink-subtle border-b border-line bg-page">
							<th class="text-left h-10 px-2">{{ 'ANALYTICS_COVERAGE_DASHBOARD.TOP_MEDIA_OUTLET_HEADER' | translate }}</th>
							@for (col of matrixColumns; track col.key) {
							<th class="h-10 px-2" [style.color]="col.color">{{ col.label }}</th>
							}
						</tr>
					</thead>
					<tbody>
						@for (row of mediaBrandMatrix; track row.mediaId) {
						<tr class="border-t border-line hover:bg-surface-blue transition-colors">
							<td class="text-left font-medium text-ink py-1.5 px-2 truncate max-w-[130px]">
								<div class="flex items-center justify-between">
									<span>{{ row.media }}</span>
									<span class="caption text-ink-subtle font-normal mr-2">({{ fmtInt(row.total) }})</span>
								</div>
							</td>
							@for (col of matrixColumns; track col.key) {
							<td class="py-1.5 px-1">
								<span
									[title]="matrixCellTitle(row.media, col.label, cellFor(row, col.key))"
									class="inline-block w-8 py-0.5 rounded-lg text-center transition-transform hover:scale-105 cursor-default"
									[class]="getImpactHeatmapBg(cellFor(row, col.key).count)">
									{{ fmtInt(cellFor(row, col.key).count) }}
								</span>
							</td>
							}
						</tr>
						}
						@if (!loading && mediaBrandMatrix.length === 0) {
						<tr><td [attr.colspan]="matrixColumns.length + 1" class="py-4 caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.NO_OUTLET_DATA' | translate }}</td></tr>
						}
					</tbody>
				</table>
			</div>

			<div class="flex items-center justify-between caption text-ink-subtle font-normal pt-2 border-t border-line">
				<div class="flex items-center gap-1.5">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.FEWER_LABEL' | translate }}</span>
					<span class="w-3 h-3 rounded-sm bg-seq-2"></span>
					<span class="w-3 h-3 rounded-sm bg-seq-3"></span>
					<span class="w-3 h-3 rounded-sm bg-seq-4"></span>
					<span class="w-3 h-3 rounded-sm bg-seq-5"></span>
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.MORE_MENTIONS_LABEL' | translate }}</span>
					<span class="text-ink-subtle text-[9px] hidden sm:inline">{{ 'ANALYTICS_COVERAGE_DASHBOARD.CELLS_HINT' | translate }}</span>
				</div>
				<button type="button" (click)="onNavigate('media')" class="text-blue hover:underline font-medium flex items-center gap-0.5 cursor-pointer">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.VIEW_FULL_MATRIX' | translate }}</span>
					<mat-icon class="!w-3 !h-3 !text-xs">arrow_forward</mat-icon>
				</button>
			</div>
		</div>

		<!-- CHART 4: Top Media -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[320px]">
			<div>
				<div class="flex items-center justify-between mb-2">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.TOP_MEDIA_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.TOP_MEDIA"></app-info-icon>
					</div>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.RANKED_BY_VOLUME' | translate }}</span>
				</div>
			</div>

			<div class="overflow-x-auto flex-1">
				<table class="w-full text-left text-[10px]">
					<thead>
						<tr class="border-b border-line overline-label text-ink-subtle">
							<th class="pb-1 w-4">#</th>
							<th class="pb-1">{{ 'ANALYTICS_GLOSSARY.MEDIA' | translate }}</th>
							<th class="pb-1 text-center">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
							<th class="pb-1 text-right">{{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}</th>
							<th class="pb-1 text-right">{{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}</th>
						</tr>
					</thead>
					<tbody class="text-ink-muted">
						@for (row of topMediaData; track row.rank) {
						<tr class="border-t border-line hover:bg-surface-blue transition-colors">
							<td class="py-1 font-medium text-ink-subtle">{{ row.rank }}</td>
							<td class="py-1 font-medium text-ink truncate max-w-[95px]">
								{{ row.media }}
							</td>
							<td class="py-1 text-center">
								<div class="flex items-center gap-1 justify-center">
									<div class="w-8 bg-neutral-tint h-2 rounded-full overflow-hidden">
										<div class="bg-blue h-full rounded-full" [style.width.%]="row.barPercent"></div>
									</div>
									<span class="font-medium text-ink text-[10px] tabular-nums">{{ fmtInt(row.mentions) }}</span>
								</div>
							</td>
							<td class="py-1 text-right font-medium text-ink-muted tabular-nums">{{ row.reach }}</td>
							<td class="py-1 text-right font-medium">
								<span class="px-1 py-0.2 rounded-full text-[10px] tabular-nums" [class]="toneClasses(row.impactTone)">
									{{ row.impact }}
								</span>
							</td>
						</tr>
						}
						@if (!loading && topMediaData.length === 0) {
						<tr><td colspan="5" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.NO_OUTLET_DATA' | translate }}</td></tr>
						}
					</tbody>
				</table>
			</div>

			<div class="flex items-center justify-end caption text-ink-subtle font-normal pt-2 border-t border-line">
				<button type="button" (click)="onNavigate('media')" class="text-blue hover:underline font-medium flex items-center gap-0.5 cursor-pointer">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.VIEW_ALL_MEDIA' | translate }}</span>
					<mat-icon class="!w-3 !h-3 !text-xs">arrow_forward</mat-icon>
				</button>
			</div>
		</div>
	</div>

	<!-- ROW 4: LONG TAIL -->
	<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
		<!-- CHART 9: Top Authors -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[300px]">
			<div>
				<div class="flex items-center justify-between mb-1">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.TOP_AUTHORS_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.TOP_AUTHORS"></app-info-icon>
					</div>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.RANKED_BY_VOLUME' | translate }}</span>
				</div>
				<p class="caption text-ink-subtle font-normal mb-2">{{ 'ANALYTICS_COVERAGE_DASHBOARD.TOP_AUTHORS_DESC' | translate }}</p>
			</div>

			<div class="overflow-x-auto flex-1">
				<table class="w-full text-left text-[10px]">
					<thead>
						<tr class="border-b border-line overline-label text-ink-subtle">
							<th class="pb-1 w-4">#</th>
							<th class="pb-1">{{ 'ANALYTICS_COVERAGE_DASHBOARD.AUTHOR_OUTLET_HEADER' | translate }}</th>
							<th class="pb-1 text-center">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
							<th class="pb-1 text-right">{{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}</th>
							<th class="pb-1 text-right">{{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}</th>
						</tr>
					</thead>
					<tbody class="text-ink-muted">
						@for (a of topAuthorsData; track a.rank) {
						<tr class="border-t border-line hover:bg-surface-blue transition-colors">
							<td class="py-1 font-medium text-ink-subtle">{{ a.rank }}</td>
							<td class="py-1 font-medium text-ink truncate max-w-[110px]">
								<div>{{ a.author }}</div>
								<div class="caption text-ink-subtle font-normal">{{ a.media }}</div>
							</td>
							<td class="py-1 text-center">
								<div class="flex items-center gap-1 justify-center">
									<div class="w-7 bg-neutral-tint h-2 rounded-full overflow-hidden">
										<div class="bg-blue h-full rounded-full" [style.width.%]="a.barPercent"></div>
									</div>
									<span class="font-medium text-ink tabular-nums">{{ fmtInt(a.mentions) }}</span>
								</div>
							</td>
							<td class="py-1 text-right font-medium text-ink-muted tabular-nums">{{ a.reach }}</td>
							<td class="py-1 text-right font-medium">
								<span class="px-1 py-0.2 rounded-full text-[10px] tabular-nums" [class]="toneClasses(a.impactTone)">
									{{ a.impact }}
								</span>
							</td>
						</tr>
						}
						@if (!loading && topAuthorsData.length === 0) {
						<tr><td colspan="5" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.NO_AUTHOR_DATA' | translate }}</td></tr>
						}
					</tbody>
				</table>
			</div>

			<div class="flex items-center justify-end caption text-ink-subtle font-normal pt-2 border-t border-line">
				<button type="button" (click)="onNavigate('media')" class="text-blue hover:underline font-medium flex items-center gap-0.5 cursor-pointer">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.VIEW_ALL_AUTHORS' | translate }}</span>
					<mat-icon class="!w-3 !h-3 !text-xs">arrow_forward</mat-icon>
				</button>
			</div>
		</div>

		<!-- CHART 10: Source Type -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[300px]">
			<div>
				<div class="flex items-center justify-between mb-1">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.SOURCE_TYPE_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.SOURCE_TYPE"></app-info-icon>
					</div>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_DASHBOARD.EDITORIAL_VS_PR' | translate }}</span>
				</div>
				<p class="caption text-ink-subtle font-normal mb-1">{{ 'ANALYTICS_COVERAGE_DASHBOARD.AI_CLASSIFIED_DESC' | translate }}</p>
			</div>

			<div class="flex-1 flex flex-col justify-center space-y-2 py-1">
				<div class="space-y-2 text-[10px]">
					@for (s of sourceTypeData; track s.name) {
					<div class="space-y-0.5">
						<div class="flex items-center justify-between">
							<div class="flex items-center gap-1.5 min-w-0">
								<span class="w-2 h-2 rounded-full shrink-0" [style.background-color]="s.color"></span>
								<span class="text-ink font-medium truncate text-[11px]">{{ s.name }}</span>
							</div>
							<div class="flex items-center gap-1.5 shrink-0">
								<span class="font-medium text-ink text-[11px] tabular-nums">{{ fmtInt(s.value) }}</span>
								<span class="text-ink-muted font-medium text-[10px] bg-surface-gray px-1.5 py-0.2 rounded-full">
									{{ s.percentage }}
								</span>
							</div>
						</div>
						<div class="w-full bg-neutral-tint rounded-full h-2 overflow-hidden">
							<div class="h-full rounded-full transition-all duration-300" [style.width.%]="(s.value / maxSourceTypeValue) * 100" [style.background-color]="s.color"></div>
						</div>
					</div>
					}
					@if (!loading && sourceTypeData.length === 0) {
					<div class="text-ink-subtle text-center py-2">{{ 'ANALYTICS_COVERAGE_DASHBOARD.NO_SOURCE_TYPE_DATA' | translate }}</div>
					}
				</div>
			</div>

			<div class="flex items-center justify-end caption text-ink-subtle font-normal pt-2 border-t border-line">
				<span class="flex-1">{{ sourceTypeSummaryLine }}</span>
				<button type="button" (click)="onNavigate('mentions')" class="text-blue hover:underline font-medium flex items-center gap-0.5 cursor-pointer shrink-0">
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.VIEW_SOURCE_DETAILS' | translate }}</span>
					<mat-icon class="!w-3 !h-3 !text-xs">arrow_forward</mat-icon>
				</button>
			</div>
		</div>

		<!-- CHART 11: Coverage Calendar -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[300px]">
			<div>
				<div class="flex items-center justify-between mb-2">
					<div class="flex items-center gap-1.5">
						<h3 class="section-title">{{ 'ANALYTICS_COVERAGE_DASHBOARD.COVERAGE_CALENDAR_TITLE' | translate }}</h3>
						<app-info-icon key="ANALYTICS_INFO.COVERAGE_DASHBOARD.COVERAGE_CALENDAR"></app-info-icon>
					</div>
				</div>
			</div>

			<div class="grid grid-cols-7 gap-1 text-center text-[9px] font-medium text-ink-subtle mb-1">
				<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.CAL_MON' | translate }}</span><span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.CAL_TUE' | translate }}</span><span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.CAL_WED' | translate }}</span><span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.CAL_THU' | translate }}</span><span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.CAL_FRI' | translate }}</span><span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.CAL_SAT' | translate }}</span><span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.CAL_SUN' | translate }}</span>
			</div>

			<div class="grid grid-cols-7 gap-1 text-[9px] flex-1">
				@for (blank of calendarLeadingBlanks; track $index) {
				<div></div>
				}
				@for (cd of calendarDays; track cd.date) {
				<div
					[title]="calendarDayTitle(cd)"
					class="py-1 rounded-lg text-center flex flex-col items-center justify-center cursor-pointer transition-transform hover:scale-105"
					[class]="getCalendarHeatmap(cd.val)">
					<span class="font-medium text-[10px] tabular-nums">{{ cd.day }}</span>
				</div>
				}
			</div>

			<div class="flex items-center justify-between caption text-ink-subtle font-normal pt-2 border-t border-line">
				<div class="flex items-center gap-1.5">
					<span>0</span>
					<span class="w-3 h-3 rounded-sm bg-seq-2"></span>
					<span class="w-3 h-3 rounded-sm bg-seq-4"></span>
					<span class="w-3 h-3 rounded-sm bg-seq-5"></span>
					<span>{{ 'ANALYTICS_COVERAGE_DASHBOARD.LEGEND_THREE_PLUS' | translate }}</span>
				</div>
			</div>
		</div>
	</div>
	</div>
	}
</div>
